import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { Logo } from '../../components/brand/Logo'
import { Icon, type IconName } from '../../components/ui/icons'
import { CLINIC, WHATSAPP_URL } from '../../config/clinic'

/**
 * Landing pública de captación — estructura según
 * docs/prompt-diseno-cima-sports.md. Los formularios crean leads en el CRM.
 *
 * Clave visual **luminosa** (feedback del cliente 08-09: los fondos azul oscuro
 * "cansan la vista"): blanco y #F4FAFD con acentos celestes, texto #2E3E4A y
 * CTAs tipo píldora en `brand-600` (el único tramo del celeste que cumple AA
 * bajo texto blanco). Sin bloques oscuros. Las formas orgánicas de fondo son
 * SVG propios inspirados en farr.cl — inspiración, no copia.
 *
 * Datos de contacto pendientes del cliente marcados [TODO-NEGOCIO].
 */

// Datos de contacto compartidos con los impresos clínicos (TODO-NEGOCIO: reales).
const PHONE_DISPLAY = CLINIC.phone
const ADDRESS = CLINIC.address

/**
 * Mapa de referencia (OpenStreetMap, sin API key ni cookies de terceros).
 * TODO-NEGOCIO: confirmar dirección exacta y coordenadas de la sede; el
 * encuadre actual es referencial sobre Gran Avenida, San Miguel.
 */
const MAP_EMBED_URL =
  'https://www.openstreetmap.org/export/embed.html?bbox=-70.6605%2C-33.5045%2C-70.6445%2C-33.4945&layer=mapnik'
const MAP_LINK_URL = 'https://www.openstreetmap.org/#map=16/-33.4995/-70.6525'

/** Píldora de llamada a la acción, sólida (primaria) o de contorno (secundaria). */
function ctaClass(kind: 'primary' | 'secondary'): string {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-sm font-semibold transition-colors'
  return kind === 'primary'
    ? `${base} bg-brand-600 text-white hover:bg-brand-700`
    : `${base} border border-brand-300 bg-white text-brand-700 hover:border-brand-500 hover:bg-soft`
}

/**
 * Formas orgánicas de fondo. Decorativas: se pintan muy tenues para dar
 * profundidad sin romper la luminosidad ni el contraste del texto encima.
 */
function Blobs({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
      viewBox="0 0 1200 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      focusable="false"
    >
      <path
        fill="#EAF6FC"
        d="M980 -40c120 60 190 190 175 320-15 130-115 260-245 300-130 40-290-10-380-110S420 210 500 120 860-100 980-40Z"
      />
      <path
        fill="#C7E7F6"
        opacity="0.45"
        d="M1120 120c70 90 60 230-20 310s-230 100-330 45S620 300 675 210s180-150 280-150 125 20 165 60Z"
      />
      <path
        fill="#F4FAFD"
        d="M120 380c90-70 240-60 320 20s90 230 20 320-220 110-320 40S30 450 120 380Z"
      />
    </svg>
  )
}

function LeadForm({ type }: { type: 'PATIENT' | 'COMPANY' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', companyName: '', message: '' })
  const [state, setState] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle')

  async function submit(e: FormEvent) {
    e.preventDefault()
    setState('sending')
    try {
      await axios.post('/api/public/leads', {
        type,
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        companyName: type === 'COMPANY' ? form.companyName || null : null,
        message: form.message || null,
        source: 'web',
      })
      setState('ok')
    } catch {
      setState('error')
    }
  }

  if (state === 'ok') {
    return (
      <p className="flex items-center justify-center gap-2 rounded-xl bg-emerald-50 p-6 text-center font-medium text-ink">
        <Icon name="check" className="h-5 w-5 shrink-0 text-emerald-600" />
        ¡Recibido! Te contactaremos a la brevedad{type === 'COMPANY' ? ' con tu cotización' : ''}.
      </p>
    )
  }

  const inputCls = 'w-full rounded-lg border border-steel-200 bg-white px-3.5 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <input required className={inputCls} placeholder={type === 'COMPANY' ? 'Nombre de contacto' : 'Nombre'} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className={inputCls} placeholder="Teléfono (WhatsApp)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <input type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        {type === 'COMPANY' && (
          <input required className={inputCls} placeholder="Empresa" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
        )}
      </div>
      <textarea
        rows={3}
        className={inputCls}
        placeholder={type === 'COMPANY' ? '¿Qué exámenes necesitas y para cuántos trabajadores?' : 'Cuéntanos tu motivo de consulta (opcional)'}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
      />
      {state === 'error' && <p className="text-sm text-red-600">No pudimos enviar el formulario, intenta de nuevo.</p>}
      <button type="submit" disabled={state === 'sending'} className={`${ctaClass('primary')} w-full disabled:opacity-60`}>
        {state === 'sending' ? 'Enviando…' : type === 'COMPANY' ? 'Solicitar cotización' : 'Solicitar hora'}
      </button>
    </form>
  )
}

interface PublicBattery {
  id: string
  name: string
  jobProfile?: string | null
  items: Array<{ service: { name: string } }>
}

/**
 * Cotizador de empresas conectado al ciclo real: crea lead + cotización
 * (COT-n) que el equipo responde; la empresa la gestiona luego en su portal.
 */
function CompanyQuoteForm() {
  const [batteries, setBatteries] = useState<PublicBattery[]>([])
  const [quantities, setQuantities] = useState<Record<string, string>>({})
  const [form, setForm] = useState({ contactName: '', email: '', phone: '', companyName: '', message: '' })
  const [state, setState] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle')
  const [code, setCode] = useState('')

  useEffect(() => {
    axios
      .get<PublicBattery[]>('/api/public/quotes/batteries')
      .then(({ data }) => setBatteries(data))
      .catch(() => setBatteries([]))
  }, [])

  const items = Object.entries(quantities)
    .filter(([, qty]) => Number(qty) > 0)
    .map(([batteryId, qty]) => ({ batteryId, quantity: Number(qty) }))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setState('sending')
    try {
      const { data } = await axios.post('/api/public/quotes', {
        contactName: form.contactName,
        contactEmail: form.email,
        contactPhone: form.phone || null,
        companyName: form.companyName,
        message: form.message || null,
        items,
      })
      setCode(data.code)
      setState('ok')
    } catch {
      setState('error')
    }
  }

  if (state === 'ok') {
    return (
      <div className="rounded-xl bg-emerald-50 p-6 text-center">
        <p className="flex items-center justify-center gap-2 font-display text-lg font-semibold text-ink">
          <Icon name="check" className="h-5 w-5 text-emerald-600" />
          Cotización {code} recibida
        </p>
        <p className="mt-1 text-sm text-ink">
          Te enviaremos la propuesta con precios y condiciones a tu correo. Al aceptar, tu empresa recibe acceso al
          portal para seguir sus exámenes en línea.
        </p>
      </div>
    )
  }

  const inputCls = 'w-full rounded-lg border border-steel-200 bg-white px-3.5 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
  return (
    <form onSubmit={submit} className="space-y-3">
      {batteries.length > 0 && (
        <div className="space-y-2 rounded-lg bg-soft/60 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-steel-600">Baterías y cantidad de trabajadores</p>
          {batteries.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{b.name}</p>
                <p className="truncate text-xs text-gray-500">{b.jobProfile ?? b.items.map((i) => i.service.name).join(', ')}</p>
              </div>
              <input
                type="number"
                min={0}
                placeholder="0"
                aria-label={`Cantidad para ${b.name}`}
                className="w-20 rounded-lg border border-steel-200 px-2.5 py-1.5 text-center text-sm focus:border-brand-500 focus:outline-none"
                value={quantities[b.id] ?? ''}
                onChange={(e) => setQuantities({ ...quantities, [b.id]: e.target.value })}
              />
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <input required className={inputCls} placeholder="Nombre de contacto" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
        <input required className={inputCls} placeholder="Empresa" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
        <input required type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input className={inputCls} placeholder="Teléfono (WhatsApp)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </div>
      <textarea
        rows={2}
        className={inputCls}
        placeholder="Cargos, faena, plazos u otros requerimientos (opcional)"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
      />
      {state === 'error' && <p className="text-sm text-red-600">No pudimos enviar la cotización, intenta de nuevo.</p>}
      <button
        type="submit"
        disabled={state === 'sending' || items.length === 0}
        className={`${ctaClass('primary')} w-full disabled:opacity-60`}
      >
        {state === 'sending' ? 'Enviando…' : items.length === 0 ? 'Indica al menos una batería' : 'Solicitar cotización'}
      </button>
      <p className="text-center text-xs text-gray-500">
        ¿Tu empresa ya trabaja con nosotros?{' '}
        <Link to="/empresa/login" className="font-medium text-brand-700 hover:underline">Entra al portal de empresas</Link>
      </p>
    </form>
  )
}

/** Cada tarjeta lleva al paso 1 del agendamiento con el motivo ya elegido (o a Empresas). */
const NEEDS: Array<{ icon: IconName; title: string; text: string; href: string }> = [
  { icon: 'injury', title: 'Tratar una lesión', text: 'Traumatología deportiva con respaldo de imagenología avanzada.', href: '/agendar?necesidad=rodilla' },
  { icon: 'mri', title: 'Hacerme una resonancia', text: 'Resonancia magnética con IA: mejor imagen, menos tiempo.', href: '#contacto' },
  { icon: 'athlete', title: 'Evaluación deportiva', text: 'Chequeo precompetitivo para entrenar y competir seguro.', href: '/agendar?necesidad=evaluacion-deportiva' },
  { icon: 'preoccupational', title: 'Exámenes para mi empresa', text: 'Baterías preocupacionales por cargo y riesgo, con convenios.', href: '#empresas' },
]

const SERVICES: Array<{ icon: IconName; title: string; text: string }> = [
  { icon: 'sportsMedicine', title: 'Medicina deportiva', text: 'Diagnóstico y tratamiento integral del deportista amateur y federado.' },
  { icon: 'trauma', title: 'Traumatología especializada', text: 'Lesiones de rodilla, hombro, tobillo y columna.' },
  { icon: 'surgery', title: 'Cirugía de lesiones deportivas', text: 'Artroscopia y cirugía reconstructiva en pabellones propios.' },
  { icon: 'rehab', title: 'Kinesiología y rehabilitación', text: 'Reintegro deportivo con plan de etapas y seguimiento.' },
  { icon: 'imaging', title: 'Imagenología', text: 'Resonancia con IA, scanner, rayos X y laboratorio en la misma sede.' },
  { icon: 'checkup', title: 'Evaluaciones precompetitivas', text: 'Aptitud física certificada para competir y para trabajar.' },
]

const PROGRAMS = [
  { name: 'Check-up Deportivo Cima', text: 'Evaluación integral para partir o retomar el entrenamiento: médico + ECG + laboratorio.' },
  { name: 'Convenio Deportivo', text: 'Para clubes y colegios: tarifas preferentes y prioridad de agenda para sus deportistas.' },
  { name: 'Reintegro Deportivo', text: 'Programa por etapas post lesión: de la consulta al retorno completo a la competencia.' },
]

const HERO_HIGHLIGHTS: Array<{ icon: IconName; label: string }> = [
  { icon: 'mri', label: 'Resonancia con IA' },
  { icon: 'fast', label: 'Informes en 48 h' },
  { icon: 'hospital', label: '3 pabellones propios' },
  { icon: 'agreement', label: 'Fonasa, Isapre y particular' },
]

// TODO-NEGOCIO: convenios y previsiones reales con los que opera Cima Sport,
// y sus logos autorizados. Mientras tanto se muestran como sellos de texto.
const COVERAGES = ['Fonasa nivel 1', 'Isapre — libre elección', 'Particular', 'Convenios de empresa', 'Clubes deportivos']

/**
 * Testimonios de demostración. NO son reseñas reales: van rotulados como demo
 * en la propia tarjeta para que nadie los confunda con opiniones de pacientes.
 * TODO-NEGOCIO: reemplazar por testimonios reales con consentimiento escrito.
 */
const DEMO_TESTIMONIALS = [
  {
    quote: 'Volví a correr cuatro meses después de la operación de rodilla, con un plan claro semana a semana.',
    author: 'Paciente de ejemplo, 34 años',
    context: 'Reintegro deportivo',
  },
  {
    quote: 'Coordinamos los preocupacionales de toda la faena en una semana y los certificados llegaron en línea.',
    author: 'Empresa de ejemplo',
    context: 'Convenio preocupacional',
  },
]

/** Título de sección con el mismo ritmo tipográfico en toda la página. */
function SectionTitle({ eyebrow, title, text }: { eyebrow?: string; title: string; text?: string }) {
  return (
    <div className="mb-8 max-w-2xl">
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-widest text-brand-600">{eyebrow}</p>}
      <h2 className="mt-1 font-display text-2xl font-bold text-ink md:text-3xl">{title}</h2>
      {text && <p className="mt-2 text-steel-600">{text}</p>}
    </div>
  )
}

export function LandingPage() {
  return (
    <div className="bg-white font-sans text-ink">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-steel-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <Logo className="h-10" />
          <nav className="hidden items-center gap-5 text-sm font-medium text-steel-700 lg:flex">
            <a href="#servicios" className="hover:text-brand-700">Servicios</a>
            <a href="#imagenologia" className="hover:text-brand-700">Imagenología</a>
            <a href="#programas" className="hover:text-brand-700">Programas</a>
            <a href="#empresas" className="hover:text-brand-700">Empresas</a>
            <a href="#equipo" className="hover:text-brand-700">Equipo médico</a>
            <a href="#visitanos" className="hover:text-brand-700">Visítanos</a>
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-1.5 text-sm font-semibold text-ink md:inline-flex">
              <Icon name="phone" className="h-4 w-4 text-brand-600" />
              {PHONE_DISPLAY}
            </span>
            <Link to="/agendar" className={`${ctaClass('primary')} px-5 py-2`}>Reserva tu hora</Link>
          </div>
        </div>
      </header>

      {/* Hero — clave luminosa con formas orgánicas de fondo */}
      <section className="relative overflow-hidden bg-surface">
        <Blobs className="opacity-70" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 md:grid-cols-[1.05fr_0.95fr] md:py-24">
          <div className="space-y-6">
            <p className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Icon name="shield" className="h-4 w-4" />
              Traumatología deportiva · Salud ocupacional
            </p>
            <h1 className="font-display text-4xl font-bold leading-tight text-ink md:text-5xl">
              Unidad especializada en <span className="text-brand-600">Traumatología Deportiva</span> y Preocupacional
            </h1>
            <p className="max-w-xl text-lg text-steel-700">
              Mejórate con un equipo de excelencia: vasta experiencia en patología traumática de alta
              complejidad y deportiva.
            </p>
            <p className="flex max-w-xl items-start gap-2 text-sm text-steel-600">
              <Icon name="hospital" className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
              <span>
                Con el respaldo de <a href="https://cimasalud.cl" className="font-medium text-brand-700 hover:underline">Clínica Cima Salud</a>:
                resonancia magnética con IA, scanner, rayos X, laboratorio y pabellones propios en San Miguel.
              </span>
            </p>
            <div className="flex flex-wrap gap-3">
              {/* La reserva en línea pasa a ser el camino principal; WhatsApp
                  sigue visible como alternativa (pedido de Chejo, 16-09). */}
              <Link to="/agendar" className={ctaClass('primary')}>
                <Icon name="calendarPlus" className="h-4 w-4" />
                Reserva tu hora en línea
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={ctaClass('secondary')}>
                <Icon name="whatsapp" className="h-4 w-4" />
                O agenda por WhatsApp
              </a>
            </div>
            <a href="#empresas" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline">
              <Icon name="company" className="h-4 w-4" />
              Servicios para empresas
            </a>
          </div>

          {/* Espacio para fotografía real del equipo/pacientes */}
          <div className="relative">
            <div className="overflow-hidden rounded-[2rem] border border-brand-100 bg-white shadow-[0_18px_50px_-30px_rgba(18,121,166,0.55)]">
              <div className="flex aspect-[4/5] flex-col items-center justify-center gap-3 bg-gradient-to-br from-soft via-white to-brand-100 p-8 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-brand-500 shadow-sm">
                  <Icon name="athlete" className="h-8 w-8" />
                </span>
                <p className="font-display text-sm font-semibold text-brand-700">Fotografía del equipo y pacientes</p>
                <p className="max-w-[16rem] text-xs text-steel-600">
                  [TODO-NEGOCIO] Reemplazar por fotografía real de la unidad (personas, box o rehabilitación),
                  con autorización de uso de imagen.
                </p>
              </div>
              <ul className="grid grid-cols-2 gap-px bg-steel-100">
                {HERO_HIGHLIGHTS.map((h) => (
                  <li key={h.label} className="flex items-center gap-2 bg-white px-4 py-3 text-xs font-medium text-steel-700">
                    <Icon name={h.icon} className="h-4 w-4 shrink-0 text-brand-600" />
                    {h.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Franja de previsión y convenios */}
      <div className="border-y border-steel-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-steel-500">
            Atendemos con
          </p>
          <ul className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {COVERAGES.map((c) => (
              <li
                key={c}
                className="flex items-center gap-2 rounded-xl border border-steel-100 bg-surface px-4 py-2.5 text-sm font-semibold text-steel-700"
              >
                {/* TODO-NEGOCIO: logotipo autorizado de cada previsión/convenio */}
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-brand-500 shadow-sm">
                  <Icon name="shield" className="h-4 w-4" />
                </span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ¿Qué necesitas? */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <div className="mb-8 flex items-start justify-center gap-4">
          {/* Personaje 3D de la marca (docs/BRAND.md). Solo desde md: en móvil
              le quitaría el espacio a las tarjetas, que son lo accionable. */}
          <img
            src="/brand/personaje/apuntando.webp"
            alt="Asistente de Cima Sport señalando las opciones de atención"
            width={232}
            height={500}
            loading="lazy"
            decoding="async"
            className="hidden h-40 w-auto select-none md:block lg:h-48"
          />
          <h2 className="text-center font-display text-2xl font-bold text-ink md:mt-8 md:text-3xl">¿Qué necesitas hoy?</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {NEEDS.map((n) => (
            <a
              key={n.title}
              href={n.href}
              className="group rounded-2xl border border-steel-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-brand-600 transition-colors group-hover:bg-brand-100">
                <Icon name={n.icon} className="h-5 w-5" />
              </span>
              <p className="mt-3 font-display font-semibold text-ink">{n.title}</p>
              <p className="mt-1 text-sm text-steel-600">{n.text}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Barra de confianza */}
      <div className="border-y border-steel-100 bg-surface">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-10 gap-y-2 px-4 py-4 text-sm font-semibold text-steel-700">
          {['Fonasa nivel 1 y particulares', 'Resonancia con IA', 'Informes en 48 h', 'Pabellones propios'].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <Icon name="check" className="h-4 w-4 text-brand-600" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Servicios */}
      <section id="servicios" className="mx-auto max-w-6xl px-4 py-14">
        <SectionTitle eyebrow="Servicios" title="Del diagnóstico a la vuelta a la cancha" text="Todo en un mismo lugar, con el equipo tratándote de principio a fin." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <div key={s.title} className="rounded-2xl border border-steel-100 bg-white p-5 shadow-sm">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-brand-600">
                <Icon name={s.icon} className="h-5 w-5" />
              </span>
              <p className="mt-3 font-display font-semibold text-ink">{s.title}</p>
              <p className="mt-1 text-sm text-steel-600">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Empresas */}
      <section id="empresas" className="relative overflow-hidden bg-surface">
        <Blobs className="opacity-60" />
        <div className="relative mx-auto max-w-6xl px-4 py-14">
          <SectionTitle
            eyebrow="Para empresas"
            title="Estudios preventivos preocupacionales"
            text="Baterías de exámenes preocupacionales y ocupacionales por cargo y riesgo, evaluaciones de aptitud laboral y convenios con tarifas por volumen. Cumplimiento Ley 16.744."
          />
          <div className="grid gap-4 md:grid-cols-4">
            {([
              { icon: 'quote', title: 'Cotiza para tu empresa', state: 'Formulario abajo', href: undefined },
              { icon: 'scheduleCheck', title: 'Agenda exámenes', state: 'Por WhatsApp', href: undefined },
              { icon: 'company', title: 'Portal de empresas', state: 'Órdenes, certificados y facturas', href: '/empresa/login' },
              { icon: 'certificate', title: 'Verificación de certificados', state: 'Verificar en línea', href: '/verificar' },
            ] as Array<{ icon: IconName; title: string; state: string; href?: string }>).map((c) => {
              const card = (
                <div className="h-full rounded-2xl border border-brand-100 bg-white p-4 shadow-sm">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-soft text-brand-600">
                    <Icon name={c.icon} className="h-5 w-5" />
                  </span>
                  <p className="mt-2.5 font-display text-sm font-semibold text-ink">{c.title}</p>
                  <p className="flex items-center gap-1 text-xs text-steel-600">
                    {c.state}
                    {c.href && <Icon name="arrowRight" className="h-3.5 w-3.5" />}
                  </p>
                </div>
              )
              return c.href ? (
                <Link key={c.title} to={c.href} className="transition-transform hover:-translate-y-0.5">{card}</Link>
              ) : (
                <div key={c.title}>{card}</div>
              )
            })}
          </div>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div className="space-y-3 text-sm text-steel-700">
              <p className="font-display text-lg font-semibold text-ink">Cotiza tus baterías preocupacionales</p>
              <p>Batería básica · Altura física · Conductores · Espacios confinados y más — armamos la batería según el perfil de riesgo de cada cargo.</p>
              {/* Páginas SEO server-rendered: enlace normal, no ruta de la SPA */}
              <p>
                <a href="/examenes" className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline">
                  Conoce qué incluye cada examen y su normativa
                  <Icon name="arrowRight" className="h-4 w-4" />
                </a>
              </p>
              <p className="text-xs text-steel-500">[TODO-NEGOCIO: cifras de capacidad y logos de empresas cliente]</p>
              {/* Personaje 3D (docs/BRAND.md). Solo desde lg: bajo ese ancho la
                  columna se apila sobre el formulario de cotización y estorba. */}
              <img
                src="/brand/personaje/presentando.webp"
                alt="Asistente de Cima Sport presentando los servicios para empresas"
                width={288}
                height={500}
                loading="lazy"
                decoding="async"
                className="hidden h-56 w-auto select-none pt-2 lg:block"
              />
            </div>
            <div className="rounded-2xl border border-steel-100 bg-white p-5 shadow-sm">
              <CompanyQuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* Imagenología */}
      <section id="imagenologia" className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid items-center gap-8 rounded-3xl border border-brand-100 bg-gradient-to-br from-white via-soft to-brand-100 p-8 md:grid-cols-2 md:p-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-600">Tecnología</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-ink md:text-3xl">Resonancia magnética con inteligencia artificial</h2>
            <p className="mt-3 text-steel-700">
              Mejor calidad de imagen en menos tiempo de examen, con informe disponible en línea.
              Además: scanner, rayos X digitales y laboratorio clínico en la misma sede.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[['48 h', 'informe'], ['IA', 'en imagen'], ['1 sede', 'todo integrado']].map(([big, small]) => (
              <div key={small} className="rounded-2xl border border-brand-100 bg-white/80 p-4 shadow-sm">
                <p className="font-display text-2xl font-bold text-brand-600">{big}</p>
                <p className="text-xs text-steel-600">{small}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programas */}
      <section id="programas" className="mx-auto max-w-6xl px-4 pb-14">
        <SectionTitle eyebrow="Programas" title="Planes pensados para volver a competir" />
        <div className="grid gap-4 md:grid-cols-3">
          {PROGRAMS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-steel-100 bg-white p-5 shadow-sm">
              <p className="font-display font-semibold text-brand-700">{p.name}</p>
              <p className="mt-1 text-sm text-steel-600">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Equipo */}
      <section id="equipo" className="border-y border-steel-100 bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <SectionTitle eyebrow="Equipo médico" title="Quiénes te atienden" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ['Dr. Sergio Mandiola', 'Traumatología — Columna'], // TODO-NEGOCIO: nombre completo
              ['Klga. Valentina Paredes', 'Kinesiología deportiva'],
              ['TM Marcelo Ibáñez', 'Imagenología — resonancia magnética'],
              ['Enf. Paula Riquelme', 'Enfermería ocupacional'],
              ['TO Diego Carrasco', 'Terapia ocupacional laboral'],
            ].map(([name, specialty]) => (
              <div key={name} className="rounded-2xl border border-steel-100 bg-white p-4 text-center shadow-sm">
                {/* TODO-NEGOCIO: retrato real de cada profesional */}
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-soft text-brand-500" aria-hidden>
                  <Icon name="personRound" className="h-7 w-7" />
                </div>
                <p className="mt-2 font-display text-sm font-semibold text-ink">{name}</p>
                <p className="text-xs text-steel-600">{specialty}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonios (demo) */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <SectionTitle eyebrow="Testimonios" title="Lo que cuentan quienes se atienden" />
        <div className="grid gap-4 md:grid-cols-2">
          {DEMO_TESTIMONIALS.map((t) => (
            <figure key={t.author} className="relative rounded-2xl border border-steel-100 bg-white p-6 shadow-sm">
              <span className="absolute right-4 top-4 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                Contenido de demostración
              </span>
              <Icon name="spark" className="h-5 w-5 text-brand-500" />
              <blockquote className="mt-3 font-display text-lg leading-snug text-ink">“{t.quote}”</blockquote>
              <figcaption className="mt-3 text-sm text-steel-600">
                {t.author} · {t.context}
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-3 text-xs text-steel-500">
          [TODO-NEGOCIO] Testimonios de ejemplo, no son reseñas reales. Reemplazar por testimonios de pacientes
          con consentimiento escrito de uso.
        </p>
      </section>

      {/* Visítanos */}
      <section id="visitanos" className="border-y border-steel-100 bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <SectionTitle eyebrow="Visítanos" title="Dónde y cuándo atendemos" />
          <div className="grid gap-6 md:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-4 rounded-2xl border border-steel-100 bg-white p-6 shadow-sm">
              <div className="flex gap-3">
                <Icon name="location" className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                <div>
                  <p className="font-display font-semibold text-ink">Dirección</p>
                  <p className="text-sm text-steel-600">{ADDRESS}</p>
                  <p className="text-xs text-steel-500">[TODO-NEGOCIO] Confirmar la dirección exacta de la unidad.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Icon name="clock" className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                <div>
                  <p className="font-display font-semibold text-ink">Horarios</p>
                  <ul className="mt-1 space-y-0.5 text-sm text-steel-600">
                    <li>Lunes a viernes · 8:00 – 19:00</li>
                    <li>Sábado · 9:00 – 13:00</li>
                    <li>Domingo y festivos · cerrado</li>
                  </ul>
                  <p className="text-xs text-steel-500">[TODO-NEGOCIO] Confirmar horarios reales de atención.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Icon name="phone" className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                <div>
                  <p className="font-display font-semibold text-ink">Contacto</p>
                  <p className="text-sm text-steel-600">{PHONE_DISPLAY}</p>
                  <p className="text-xs text-steel-500">[TODO-NEGOCIO] Teléfono y WhatsApp definitivos.</p>
                </div>
              </div>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={ctaClass('primary')}>
                <Icon name="whatsapp" className="h-4 w-4" />
                Escríbenos por WhatsApp
              </a>
            </div>
            <div className="overflow-hidden rounded-2xl border border-steel-100 bg-white shadow-sm">
              <iframe
                title="Mapa de ubicación de Cima Sport"
                src={MAP_EMBED_URL}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-72 w-full border-0 md:h-full md:min-h-[22rem]"
              />
              <p className="border-t border-steel-100 px-4 py-2 text-xs text-steel-500">
                Ubicación referencial ·{' '}
                <a href={MAP_LINK_URL} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">
                  Ver mapa completo
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Respaldo Cima Salud */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <div className="rounded-3xl border border-brand-100 bg-soft p-8">
          <h2 className="font-display text-xl font-bold text-ink">Una unidad con identidad propia, con el respaldo de Clínica Cima Salud</h2>
          <p className="mt-2 max-w-3xl text-sm text-steel-700">
            Cima Sport nace desde <a href="https://cimasalud.cl" className="font-medium text-brand-700 underline">Clínica Cima Salud</a> (Gran
            Avenida 4564, San Miguel) y opera con administración y marca propias, compartiendo su
            infraestructura clínica: imagenología de última generación, laboratorio y tres pabellones quirúrgicos.
          </p>
        </div>
      </section>

      {/* Contacto / agendamiento */}
      <section id="contacto" className="mx-auto max-w-6xl px-4 pb-16">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="space-y-4">
            <h2 className="font-display text-2xl font-bold text-ink md:text-3xl">Reserva tu hora</h2>
            <p className="text-steel-600">
              Elige tu hora en línea en tres pasos —ves las horas disponibles antes de identificarte—, o agenda por WhatsApp
              si prefieres que te ayudemos.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/agendar" className={ctaClass('primary')}>
                <Icon name="calendarPlus" className="h-4 w-4" />
                Reservar en línea
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                <Icon name="whatsapp" className="h-4 w-4" />
                Agenda por WhatsApp
              </a>
            </div>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-steel-600">
              <span className="inline-flex items-center gap-1.5"><Icon name="phone" className="h-4 w-4 text-brand-600" />{PHONE_DISPLAY}</span>
              <span className="inline-flex items-center gap-1.5"><Icon name="location" className="h-4 w-4 text-brand-600" />{ADDRESS}</span>
            </p>
          </div>
          <div className="rounded-2xl border border-steel-100 bg-white p-5 shadow-sm">
            <LeadForm type="PATIENT" />
          </div>
        </div>
      </section>

      {/* Footer — claro, con separación por línea en vez de bloque oscuro */}
      <footer className="border-t border-steel-100 bg-surface text-steel-700">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 text-sm md:grid-cols-3">
          <div>
            <Logo className="h-10" />
            <p className="mt-3">Traumatología deportiva y exámenes preocupacionales.</p>
            <p className="mt-1">{ADDRESS}</p>
          </div>
          <div>
            <p className="font-display font-semibold text-ink">Horarios</p>
            <p className="mt-1">Lun a Vie 8:00 – 19:00 · Sáb 9:00 – 13:00</p>
            <p className="mt-1">Fonasa nivel 1 · Isapre · Particular</p>
          </div>
          <div>
            <p className="font-display font-semibold text-ink">Enlaces</p>
            <p className="mt-1"><Link to="/blog" className="hover:text-brand-700">Blog de salud deportiva</Link></p>
            <p className="mt-1"><Link to="/verificar" className="hover:text-brand-700">Verificar certificado</Link></p>
            <p className="mt-1"><Link to="/verificar-documento" className="hover:text-brand-700">Verificar receta u orden</Link></p>
            <p className="mt-1"><a href="https://cimasalud.cl" className="hover:text-brand-700">Clínica Cima Salud</a></p>
            <p className="mt-1"><Link to="/login" className="hover:text-brand-700">Acceso funcionarios</Link></p>
          </div>
        </div>
        <p className="border-t border-steel-100 py-4 text-center text-xs text-steel-500">
          © {new Date().getFullYear()} Cima Sport — Todos los derechos reservados.
        </p>
      </footer>

      {/* Botón flotante de WhatsApp */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Agenda por WhatsApp"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105"
      >
        <Icon name="whatsapp" className="h-7 w-7" />
      </a>
    </div>
  )
}
